import apiClient from "./apiClient";

export type ResponsibleGamingPolicyType =
  | "DEPOSIT_LIMIT"
  | "LOSS_LIMIT"
  | "TIME_LIMIT"
  | "COOLING_OFF"
  | "SELF_EXCLUSION";

export type RecurringPeriod = "DAY" | "WEEK" | "MONTH";
export type PolicyChangeType = "UPDATE" | "DISABLE";

export interface ResponsibleGamingPolicy {
  policyId: string;
  policyType: ResponsibleGamingPolicyType;
  changeType: PolicyChangeType;
  recurringPeriod?: RecurringPeriod;
  currency?: string;
  amount?: string;
  timeLimitSeconds?: number;
  coolingOffDuration?: string;
  selfExclusionDuration?: string;
  effectiveAt?: string;
  predecessorPolicyId?: string;
}

export interface RecurringLimitOverview {
  recurringPeriod: RecurringPeriod;
  configured: boolean;
  limitReached?: boolean;
  currentWindowEnd?: string;
  currentPolicy?: ResponsibleGamingPolicy;
  pendingPolicy?: ResponsibleGamingPolicy;
  usedAmount?: string;
  reservedAmount?: string;
  remainingAmount?: string;
  usedSeconds?: number;
  remainingSeconds?: number;
}

export interface DurationPolicyOverview {
  active?: boolean;
  configured?: boolean;
  blocking?: boolean;
  currentPolicy?: ResponsibleGamingPolicy;
  pendingPolicy?: ResponsibleGamingPolicy;
  effectiveAt?: string;
  expiresAt?: string;
  minimumEndAt?: string;
  remainingSeconds?: number;
}

export interface ResponsibleGamingOverview {
  depositLimits?: RecurringLimitsContainer;
  depositLimit?: RecurringLimitsContainer;
  lossLimits?: RecurringLimitsContainer;
  lossLimit?: RecurringLimitsContainer;
  timeLimits?: RecurringLimitsContainer;
  timeLimit?: RecurringLimitsContainer;
  coolingOff?: DurationPolicyOverview;
  selfExclusion?: DurationPolicyOverview;
}

export type RecurringLimitsContainer =
  | RecurringLimitOverview[]
  | Partial<Record<RecurringPeriod, RecurringLimitOverview>>
  | { scopes?: RecurringLimitOverview[] };

export type SetResponsibleGamingPolicyPayload =
  | {
      policyType: "DEPOSIT_LIMIT" | "LOSS_LIMIT";
      recurringPeriod: RecurringPeriod;
      amount: string;
      currency: string;
    }
  | {
      policyType: "TIME_LIMIT";
      recurringPeriod: RecurringPeriod;
      timeLimitSeconds: number;
    }
  | { policyType: "COOLING_OFF"; coolingOffDuration: string }
  | { policyType: "SELF_EXCLUSION"; selfExclusionDuration: string };

const unwrap = <T>(response: unknown): T => {
  const value = response as { data?: T };
  return (value?.data ?? response) as T;
};

export const getResponsibleGamingOverview = async (userId: string) =>
  unwrap<ResponsibleGamingOverview>(
    await apiClient.get(`user/responsible-gaming/overview/${userId}`),
  );

export const setResponsibleGamingPolicy = async (
  userId: string,
  payload: SetResponsibleGamingPolicyPayload,
) => apiClient.put(`user/responsible-gaming/policy/${userId}`, payload);

export const resetResponsibleGamingPolicy = async (
  userId: string,
  policyId: string,
) =>
  apiClient.delete(
    `user/responsible-gaming/policy/${userId}/${policyId}/reset`,
    {
      data: { reason: "Responsible Gaming restriction reset from Back Office" },
    },
  );

export const getResponsibleGamingHistory = async (userId: string) =>
  apiClient.get(`user/responsible-gaming/history/${userId}`);
